/**
 * Inserts structured paragraph runs for the Agent drafting tools (Office.js).
 *
 * Each paragraph can carry inline structure metadata (list, alignment, bold) so the agent does
 * NOT have to make a separate apply_word_structure call for new content. Structure is applied
 * within the same Word.run() immediately after insertion.
 *
 * Paragraph indices in the agent API match DocumentReader.getFullDocumentContext() (non-empty only).
 */

/* global Word */

import { log, logError } from '../../utils/logging';
import { formatOfficeJsError } from '../../utils/officeErrorFormat';
import { applyStandardOutlineNumbering, applyBulletAllLevels } from './wordListOutline';
import { prependRlmToHebrewParagraphs, textLooksHebrew, trySetSectionRtl } from './wordDocumentRtl';

async function syncWithLabel(context: Word.RequestContext, label: string): Promise<void> {
  try {
    await context.sync();
    log('[AgentDocumentInserter] sync OK', label);
  } catch (e: unknown) {
    logError('[AgentDocumentInserter] sync FAILED', label, formatOfficeJsError(e));
    throw e;
  }
}

export type InsertParagraphsMode = 'fill_empty' | 'after_agent_paragraph';

/**
 * Per-paragraph structure spec. Strings are also accepted and treated as plain paragraphs.
 *
 * list: 'numbered' | 'bullet'
 *   Consecutive paragraphs sharing the same list type form one Word list.
 *   A non-list paragraph or a change in type starts a new list.
 *   ANY leading numeric/bullet prefix in `text` is auto-stripped ("1. ", "2) ", "• " …).
 *
 * level: 0-based outline level (0 = 1., 2., … — main sections; 1 = 1.1, 1.2 — sub-clauses;
 *   2 = 1.1.1, …). Required for multilevel **numbered** lists; do not use `bullet` for numbered sub-points.
 * numbering: Word `ListNumbering` (`Arabic` = standard 1,2,3 / multilevel — Word's label for Western decimals).
 * align: paragraph alignment applied after insert.
 * bold: bold the entire paragraph text.
 */
/** Agent may send `list` as a string or as `{ level, numbering }` (same reference every time would group; distinct objects did not). */
export type ParagraphSpecList =
  | 'numbered'
  | 'bullet'
  | {
      level?: number;
      numbering?: 'Arabic' | 'LowerLetter' | 'UpperLetter' | 'LowerRoman' | 'UpperRoman';
      type?: 'numbered' | 'bullet';
      bullet?: boolean;
    };

export type ParagraphSpec = {
  text: string;
  list?: ParagraphSpecList;
  level?: number;
  numbering?: 'Arabic' | 'LowerLetter' | 'UpperLetter' | 'LowerRoman' | 'UpperRoman';
  align?: 'Left' | 'Centered' | 'Right' | 'Justified';
  bold?: boolean;
};

/** After normalizeSpec, `list` is always a string or omitted (never a rich object). */
type NormalizedSpec = Required<Pick<ParagraphSpec, 'text' | 'level' | 'numbering'>> & {
  list?: 'numbered' | 'bullet';
  align?: ParagraphSpec['align'];
  bold?: ParagraphSpec['bold'];
};

/** Strip leading "1. " / "2) " / "(3) " / "א. " / "• " etc. from text before inserting into a list */
function stripListPrefix(text: string): string {
  return text
    .replace(/^\s*\(?\d+[\.\)]\s*/, '') // "1. " | "1) " | "(1) "
    .replace(/^\s*\d+\s+/, '')           // "1 " (space after digit)
    .replace(/^\s*[א-ת][\.\)]\s*/, '')  // "א. " Hebrew letter prefix
    .replace(/^\s*[•\-\*]\s*/, '')       // "• " "- " "* "
    .trim();
}

/** Party / routing lines must not get Word outline numbers (common model mistake → "everything numbered"). */
function shouldOmitListNumberingForBoilerplate(text: string, list: 'numbered' | 'bullet' | undefined, level: number): boolean {
  if (list !== 'numbered' || level !== 0) return false;
  const t = (text || '').trim();
  if (!t) return false;
  if (/^\s*(בין|לבין)\s*:/.test(t)) return true;
  if (/^\s*(Between|And)\s*:/i.test(t)) return true;
  return false;
}

function normalizeSpec(raw: string | ParagraphSpec): NormalizedSpec {
  const spec: ParagraphSpec = typeof raw === 'string' ? { text: raw } : { ...raw };
  const rawList = spec.list as ParagraphSpecList | undefined;

  let list: 'numbered' | 'bullet' | undefined;
  let level = Number(spec.level ?? 0) || 0;
  let numbering: ParagraphSpec['numbering'] = spec.numbering ?? 'Arabic';

  if (rawList === 'numbered' || rawList === 'bullet') {
    list = rawList;
  } else if (rawList && typeof rawList === 'object') {
    const o = rawList;
    if (o.type === 'bullet' || o.bullet === true) {
      list = 'bullet';
    } else {
      list = 'numbered';
      if (o.numbering != null) numbering = o.numbering;
    }
    if (o.level !== undefined) level = Number(o.level) || 0;
  }

  level = Math.max(0, Math.min(8, level));
  if (shouldOmitListNumberingForBoilerplate(spec.text ?? '', list, level)) {
    list = undefined;
  }
  const text = list ? stripListPrefix(spec.text ?? '') : (spec.text ?? '');

  return {
    ...spec,
    text,
    list,
    level,
    numbering: numbering ?? 'Arabic',
  };
}

/**
 * @param paragraphs - Strings or ParagraphSpec objects. Consecutive specs with the same `list` type
 *   form one Word list automatically. Structure is applied within the same Word.run().
 * @param mode - fill_empty | after_agent_paragraph
 * @param afterAgentParagraphIndex - Required when mode is after_agent_paragraph.
 */
export async function insertParagraphsForAgent(
  paragraphs: (string | ParagraphSpec)[],
  mode: InsertParagraphsMode,
  afterAgentParagraphIndex?: number
): Promise<{ ok: boolean; inserted: number; detail?: string }> {
  const allSpecs: NormalizedSpec[] = (Array.isArray(paragraphs) ? paragraphs : []).map(normalizeSpec);
  if (allSpecs.length === 0 || allSpecs.every((s) => !s.text.trim())) {
    return { ok: false, inserted: 0, detail: 'No non-empty paragraphs to insert' };
  }

  try {
    const specSummary = allSpecs.map((s, i) => ({
      i,
      len: s.text.length,
      list: s.list ?? null,
      level: s.level,
      align: s.align ?? null,
      bold: s.bold ?? null,
    }));
    log('[AgentDocumentInserter] start', {
      mode,
      paragraphSpecs: allSpecs.length,
      afterAgentParagraphIndex: afterAgentParagraphIndex ?? null,
      specSummarySample: specSummary.slice(0, 8),
      specSummaryTotal: specSummary.length,
    });

    await Word.run(async (context) => {
      const rtlSample = allSpecs.map((s) => s.text).join('\n');
      let sectionRtlOk = false;
      if (textLooksHebrew(rtlSample)) {
        sectionRtlOk = await trySetSectionRtl(context);
      }

      const body = context.document.body;
      const pc = body.paragraphs;
      pc.load('items');
      await syncWithLabel(context, 'load body.paragraphs.items');

      const items = pc.items as unknown as any[];
      for (const item of items) (item as any).load('text');
      await syncWithLabel(context, 'load paragraph text');

      const wordText = (i: number) => String((items[i] as any).text ?? '').trim();

      // --- Phase 1: insert all paragraphs, collect Word paragraph anchors per spec ---
      // insertedAnchors[i] corresponds to allSpecs[i]
      const insertedAnchors: any[] = new Array(allSpecs.length).fill(null);

      if (mode === 'fill_empty') {
        const hasContent = items.some((_, i) => wordText(i).length > 0);
        if (hasContent) {
          throw new Error(
            'fill_empty requires a document with no non-empty paragraphs; use after_agent_paragraph.'
          );
        }

        // Insert first spec into the existing blank paragraph (or a new one)
        let anchor: any;
        if (items.length === 0) {
          anchor = (body as any).insertParagraph(allSpecs[0].text, Word.InsertLocation.start);
        } else {
          items[0].getRange().insertText(allSpecs[0].text, Word.InsertLocation.replace);
          anchor = items[0];
        }
        insertedAnchors[0] = anchor;

        // Chain the rest
        for (let i = 1; i < allSpecs.length; i++) {
          anchor = anchor.insertParagraph(allSpecs[i].text, Word.InsertLocation.after);
          insertedAnchors[i] = anchor;
        }
        log('[AgentDocumentInserter] phase1 fill_empty chain done', { insertedCount: allSpecs.length });
      } else {
        // after_agent_paragraph
        const idx = afterAgentParagraphIndex;
        if (idx === undefined || idx === null || Number.isNaN(Number(idx))) {
          throw new Error('after_agent_paragraph_index is required for after_agent_paragraph mode');
        }
        const targetAgent = Math.max(0, Math.floor(Number(idx)));
        let agentSeen = -1;
        let targetWordIdx = -1;
        for (let wi = 0; wi < items.length; wi++) {
          if (wordText(wi).length === 0) continue;
          agentSeen++;
          if (agentSeen === targetAgent) { targetWordIdx = wi; break; }
        }
        if (targetWordIdx < 0) {
          throw new Error(
            `after_agent_paragraph_index ${targetAgent} not found (not enough non-empty paragraphs).`
          );
        }

        // Insert specs chained after the target doc paragraph
        let anchor: any = items[targetWordIdx];
        for (let i = 0; i < allSpecs.length; i++) {
          anchor = anchor.insertParagraph(allSpecs[i].text, Word.InsertLocation.after);
          insertedAnchors[i] = anchor;
        }
        log('[AgentDocumentInserter] phase1 after_agent_paragraph chain done', {
          insertedCount: allSpecs.length,
          afterIdx: afterAgentParagraphIndex,
        });
      }

      await syncWithLabel(context, 'phase1 insert paragraphs');

      // --- Phase 2: apply alignment and bold directly to anchors ---
      for (let i = 0; i < allSpecs.length; i++) {
        const spec = allSpecs[i];
        const para = insertedAnchors[i];
        if (!para) continue;
        if (spec.align) para.alignment = spec.align;
        if (spec.bold) para.font.bold = true;
      }
      await syncWithLabel(context, 'phase2 align/bold');

      // --- Phase 3: apply Word-native lists ---
      // Group consecutive same-type list items into list groups (a gap breaks the group)
      type ListGroup = { type: 'numbered' | 'bullet'; indices: number[] };
      const groups: ListGroup[] = [];
      let currentGroup: ListGroup | null = null;

      for (let i = 0; i < allSpecs.length; i++) {
        const spec = allSpecs[i];
        if (!spec.list) {
          currentGroup = null;
          continue;
        }
        if (!currentGroup || currentGroup.type !== spec.list) {
          currentGroup = { type: spec.list, indices: [] };
          groups.push(currentGroup);
        }
        currentGroup.indices.push(i);
      }

      log('[AgentDocumentInserter] phase3 list groups', {
        groupCount: groups.length,
        groups: groups.map((g) => ({ type: g.type, specIndices: g.indices, levels: g.indices.map((ix) => allSpecs[ix].level ?? 0) })),
      });

      let groupIdx = 0;
      for (const group of groups) {
        if (group.indices.length === 0) continue;
        groupIdx += 1;
        log('[AgentDocumentInserter] phase3 group start', {
          groupIdx,
          type: group.type,
          specIndices: group.indices,
          paragraphCount: group.indices.length,
        });

        // Only detach paragraphs that are already list items — detachFromList() throws GeneralException otherwise.
        for (const i of group.indices) {
          const p = insertedAnchors[i];
          if (p) (p as Word.Paragraph).load('isListItem');
        }
        await syncWithLabel(context, `phase3 g${groupIdx} load isListItem before detach`);
        for (const i of group.indices) {
          const p = insertedAnchors[i] as Word.Paragraph | undefined;
          if (p?.isListItem) {
            p.detachFromList();
          }
        }
        await syncWithLabel(context, `phase3 g${groupIdx} detachFromList (where needed)`);

        const firstPara = insertedAnchors[group.indices[0]];
        const list = firstPara.startNewList();
        await syncWithLabel(context, `phase3 g${groupIdx} startNewList`);

        if (group.type === 'numbered') {
          const num = allSpecs[group.indices[0]].numbering;
          log('[AgentDocumentInserter] phase3 apply outline numbering', { groupIdx, numbering: num });
          applyStandardOutlineNumbering(list, num);
        } else {
          applyBulletAllLevels(list, 'Solid');
        }
        await syncWithLabel(context, `phase3 g${groupIdx} setLevelNumbering/setLevelBullet (0–8)`);

        list.load('id');
        await syncWithLabel(context, `phase3 g${groupIdx} list.load id`);
        const listId = list.id;
        log('[AgentDocumentInserter] phase3 list id', { groupIdx, listId });

        // startNewList() already attaches the first paragraph — do NOT attachToList again (GeneralException).
        for (let k = 1; k < group.indices.length; k++) {
          const specIdx = group.indices[k];
          const level = allSpecs[specIdx].level ?? 0;
          insertedAnchors[specIdx].attachToList(listId, level);
        }
        await syncWithLabel(context, `phase3 g${groupIdx} attachToList (rest only, count=${Math.max(0, group.indices.length - 1)})`);
      }

      if (textLooksHebrew(rtlSample)) {
        if (!sectionRtlOk) {
          sectionRtlOk = await trySetSectionRtl(context);
        }
        if (!sectionRtlOk) {
          const n = await prependRlmToHebrewParagraphs(context);
          log('[AgentDocumentInserter] RTL RLM fallback after lists', { paragraphs: n });
        }
      }
    });

    const inserted = allSpecs.filter((s) => s.text.trim().length > 0).length;
    log('[AgentDocumentInserter] inserted', { count: inserted, mode });
    return { ok: true, inserted };
  } catch (e: unknown) {
    const msg = e instanceof Error ? e.message : String(e);
    logError('[AgentDocumentInserter] failed', {
      mode,
      msg,
      office: formatOfficeJsError(e),
    });
    return { ok: false, inserted: 0, detail: msg };
  }
}
